import { Intent, OverlayToaster, Position, Toaster } from "@blueprintjs/core"
import Papa, { ParseResult } from "papaparse"
import { ChangeEvent, useEffect, useState } from "react"
import { UseFormSetValue } from "react-hook-form"

import { createToaster } from "../../../utils/createToaster"
import useTradingInstruments from "../../Config/hooks/useTradingInstruments"
import UploadButton from "./components/UploadButton"
import { Broker } from "./types/brokers"
import { OrderType, Side } from "./types/common"
import { ExecutionType } from "./types/execution"
import { CSVOrder, WaveInputs } from "./types/form"
import {
  checkColumnsName,
  validateAuction,
  validateBroker,
  validateCAP,
  validateExecutionType,
  validateFloat,
  validateInteger,
  validateOrderType,
  validateSide,
  validateTargetEndTime,
} from "./utils/csvValidationFunctions"
import { normalizeCSVData } from "./utils/normalizeCSVData"

// Create toaster instance
const toaster: Toaster = OverlayToaster.create({ position: Position.TOP })

interface ICSVUploaderProps {
  resetWaveForm: () => void
  setValue: UseFormSetValue<WaveInputs>
}

const CSVUploader = ({ resetWaveForm, setValue }: ICSVUploaderProps) => {
  const [csvData, setCsvData] = useState<CSVOrder[] | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [tickerQuery, setTickerQuery] = useState<string>("")

  const {
    instruments,
    isLoading: isLoadingInstruments,
    error: instrumentsError,
  } = useTradingInstruments({ query: tickerQuery })

  /**
   * Effect to create a comma-separated list of tickers from the CSV data
   */
  useEffect(() => {
    if (csvData) {
      const tickers = csvData.map(row => row.TICKER).filter(ticker => ticker !== undefined)
      setTickerQuery(tickers.join(","))
    }
  }, [csvData])

  // Effect to load orders in form state
  useEffect(() => {
    if (!isLoadingInstruments && csvData) {
      const orders = csvData.map(row => {
        // Validate and parse fields
        const usd = row.USD ? validateFloat(row.USD, "USD", setErrorMessage) : undefined
        const qty = row.QTY ? validateInteger(row.QTY, "QTY", setErrorMessage) : undefined
        const timeInMarket = row["TIME IN MARKET"]
          ? validateInteger(row["TIME IN MARKET"], "TIME IN MARKET", setErrorMessage)
          : undefined
        const limit = row.LIMIT ? validateFloat(row.LIMIT, "LIMIT", setErrorMessage) : undefined
        const executionCapPct = row.CAP ? validateCAP(row.CAP, "CAP", setErrorMessage) : ""
        const broker = row.BROKER ? validateBroker(row.BROKER, setErrorMessage) : Broker.EXEC_DESK
        const executionType = row.EXECUTION
          ? validateExecutionType(row.EXECUTION, setErrorMessage)
          : ExecutionType.HERE
        const auction = row.AUCTION ? validateAuction(row.AUCTION, setErrorMessage) : false
        const orderType = row.TYPE ? validateOrderType(row.TYPE, setErrorMessage) : OrderType.MARKET
        const side = row.SIDE ? validateSide(row.SIDE, setErrorMessage) : Side.BUY
        const targetEnd = row["TARGET END"]
          ? validateTargetEndTime(row["TARGET END"], setErrorMessage)
          : ""

        const instrument = instruments?.find(
          instrument =>
            instrument.bloomberg_code.replace(" Equity", "") === row.TICKER?.replace(" Equity", ""),
        )

        return {
          amount_usd: usd,
          execution_cap_pct: executionCapPct,
          execution_details: row.COMMENT ? row.COMMENT : "",
          execution_type: executionType,
          limit_price: limit,
          broker: broker,
          auction: auction,
          order_type: orderType,
          quantity: qty,
          side: side,
          instrument: instrument,
          target_end_datetime: targetEnd,
          time_in_market: timeInMarket,
        }
      })

      // Populate the React Hook Form state
      setValue("orders", orders)
    }
  }, [csvData, setValue, instruments, isLoadingInstruments])

  // Effect to display error message in toaster
  useEffect(() => {
    if (errorMessage || instrumentsError) {
      resetWaveForm() // reset wave if error
      toaster.show({
        message: errorMessage || instrumentsError?.message,
        intent: Intent.DANGER,
      })
      setErrorMessage("") // Reset the error message after showing the toast
      return
    }
  }, [errorMessage, resetWaveForm])

  const handleCsvFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      Papa.parse(file, {
        header: true,
        delimiter: "", // auto-detect
        skipEmptyLines: "greedy", // to skip also lines that contain only delimiter (no value)
        // callback to execute when parsing is completed. It receives the parse results.
        complete: (results: ParseResult<CSVOrder>) => {
          if (results.data.length === 0) {
            createToaster({
              message: "CSV Error: The CSV is empty.",
              intent: Intent.DANGER,
            })
            return // ensures that the function stops execution at this point
          }
          const normalizedData: CSVOrder[] = normalizeCSVData(results.data)

          // Check if there's invalid column name
          const invalidColName = checkColumnsName(normalizedData)

          if (invalidColName) {
            createToaster({
              message: `CSV Error: Wrong column name: ${invalidColName.join(
                ", ",
              )}. Click on info icon near the upload CSV button to get info about columns name.`,
              intent: Intent.DANGER,
            })
            return // Stop execution
          }

          if (normalizedData.length > 100) {
            createToaster({
              message: "CSV Error: The CSV contains more than 100 orders.",
              intent: Intent.DANGER,
            })
            return // Stop execution
          }

          resetWaveForm()
          setCsvData(normalizedData)
        },
        error: () => {
          createToaster({
            message: "Error parsing the CSV file. Please verify CSV or us using intercom.",
            intent: Intent.DANGER,
          })
        },
      })
      // Reset the file input value to allow showing again toast error
      // when user try many times to upload the same files with error
      event.target.value = ""
    }
  }

  return <UploadButton onChange={handleCsvFile} />
}

export default CSVUploader
